import { useAppSelector } from '@/app/hooks'
import { selectUser } from '@/features/auth/authSlice'
import { normalizeRolePolicies } from '@/lib/auth/normalizeRolePolicies'

/**
 * Custom hook that provides permission checking based on the user's current role and policy array.
 * Mirrors Angular's `authService.hasPolicy()` behavior.
 *
 * Usage:
 * ```tsx
 * const { hasPolicy } = usePermission()
 * if (hasPolicy(['signature_removal'])) { ... }
 * ```
 */
export function usePermission() {
    const user = useAppSelector(selectUser)

    /**
     * Check if the current user has at least one of the given policy keys.
     * ROLE_ADMIN automatically has all policies.
     */
    const hasPolicy = (policyKeys: string[]): boolean => {
        if (!user?.current_role) return false

        // Admin role has all permissions
        if (user.current_role.role_name === 'ROLE_ADMIN') return true

        const userPolicies = normalizeRolePolicies(user.current_role.policy)
        if (userPolicies.length === 0) return false

        return policyKeys.some((key) => userPolicies.includes(key))
    }

    return { hasPolicy, user }
}
